import { useEffect, useState, useCallback, useMemo } from "react";
import CheckBox from "../../atoms/CheckBox/CheckBox";
import {
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { endpoints } from "../../../api/endpoints";
import {
  getApiRequestLogs,
  getApiRequestLogDetail,
  getActiveVendorCategories,
  updateVendorCategories,
  getUserById,
  loginAsUser,
} from "../../../store/slices/AdminSlice/AdminSlice";
import Grid from "../../atoms/Grid/Grid";

export default function EditUser() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    apiLogsLoading,
    selectedLogDetail,
    logDetailLoading,
    vendorCategories,
    vendorCategoriesLoading,
    updateVendorCategoriesLoading,
    selectedUser,
    selectedUserLoading,
    selectedUserError,
    loginAsLoading,
    loginAsError,
    loginAsSuccess,
  } = useSelector((state) => state.admin);

  const [permissions, setPermissions] = useState([]);
  const [userVendorCategories, setUserVendorCategories] = useState({}); // Changed to object: { vcId: quantity }
  const [formChanges, setFormChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "details";
  const setActiveTab = (tab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === "details") {
        next.delete("tab");
      } else {
        next.set("tab", tab);
      }
      return next;
    });
  };
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showLoginAsConfirm, setShowLoginAsConfirm] = useState(false);

  // Fetch user data
  useEffect(() => {
    if (id) {
      dispatch(getUserById(id));
    }
  }, [id, dispatch]);

  // Process user data when it arrives
  useEffect(() => {
    if (selectedUser) {
      // Handle both new categorized and old flat permissions structure
      const flatPermissions = [];
      if (selectedUser.permissions && Array.isArray(selectedUser.permissions)) {
        // Check if it's the new categorized format
        if (
          selectedUser.permissions.length > 0 &&
          selectedUser.permissions[0].category
        ) {
          // New categorized format
          selectedUser.permissions.forEach((category) => {
            if (category.permissions && Array.isArray(category.permissions)) {
              category.permissions.forEach((permission) => {
                flatPermissions.push({
                  ...permission,
                  category: category.category,
                });
              });
            }
          });
        } else {
          // Old flat format - add a default category
          selectedUser.permissions.forEach((permission) => {
            flatPermissions.push({
              ...permission,
              category: "General",
            });
          });
        }
      }
      setPermissions(flatPermissions);

      // Parse user's vendor categories from allowed_vcs - now an object with quantities
      const userVCs =
        selectedUser.allowed_vcs &&
        typeof selectedUser.allowed_vcs === "object" &&
        !Array.isArray(selectedUser.allowed_vcs)
          ? selectedUser.allowed_vcs
          : {};
      setUserVendorCategories(userVCs);
    }
  }, [selectedUser]);

  // Fetch vendor categories
  useEffect(() => {
    dispatch(getActiveVendorCategories());
  }, [dispatch]);

  // Load API logs when the logs tab is active - now handled by Grid component
  // useEffect(() => {
  //   if (activeTab === "logs" && id) {
  //     dispatch(
  //       getApiRequestLogs({ user_id: id, ordering: "-date,-start_time" })
  //     );
  //   }
  // }, [activeTab, id, dispatch]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormChanges((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      // Merge original user data with changes
      const formData = { ...selectedUser, ...formChanges };

      // First update user details and permissions
      const url =
        endpoints.MITCO_API_URL +
        endpoints.CSFA_BASE +
        endpoints.CSFA_SITE_ADMIN +
        `${id}/update_user/`;
      // Compose payload
      const payload = {
        ...formData,
        permissions: permissions.map((p) => ({
          name: p.permission_name,
          has_permission: p.has_permission,
        })),
      };
      await axios.put(url, payload, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      // Then update vendor categories using Redux - now sending object with quantities
      await dispatch(
        updateVendorCategories({
          userId: id,
          vendorCategories: userVendorCategories,
        }),
      ).unwrap();

      setSuccess("User updated successfully!");
      // Refresh user data to get updated values
      dispatch(getUserById(id));
    } catch (err) {
      setError(
        err?.message || err?.detail || err?.error || "Failed to update user.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleteError("");
    try {
      const url =
        endpoints.MITCO_API_URL +
        endpoints.CSFA_BASE +
        endpoints.CSFA_SITE_ADMIN +
        `${id}/delete_user/`;
      await axios.delete(url, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      navigate(-1); // Go back to user list after delete
    } catch (err) {
      setDeleteError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          "Failed to delete user.",
      );
    }
  };

  const handleLoginAs = async () => {
    setShowLoginAsConfirm(true);
  };

  const confirmLoginAs = async () => {
    setShowLoginAsConfirm(false);
    try {
      await dispatch(loginAsUser(id)).unwrap();
      // Redirect to home page after successful login
      navigate("/", { replace: true });
    } catch (err) {
      // Error is handled by Redux, we don't need to do anything here
      console.error("Login as user failed:", err);
    }
  };

  const handleViewDetails = useCallback(
    (logId) => {
      dispatch(getApiRequestLogDetail(logId));
      setShowDetailModal(true);
    },
    [dispatch],
  );

  const closeDetailModal = () => {
    setShowDetailModal(false);
  };

  // Memoize Grid configuration for API logs
  const logsHeaderAttributes = useMemo(
    () => [
      {
        id: "date_time",
        label: "Date & Time",
        sortable: true,
        searchable: {
          label: "Date & Time",
          type: "datetime-range",
          fromLabel: "From Date & Time",
          toLabel: "To Date & Time",
          inputType: "datetime-local",
        },
        width: "180px",
      },
      {
        id: "method",
        label: "Method",
        sortable: true,
        searchable: { label: "Method" },
        width: "100px",
      },
      {
        id: "endpoint",
        label: "Request",
        sortable: true,
        searchable: { label: "Request" },
        width: "280px",
      },
      {
        id: "referer",
        label: "Source",
        sortable: true,
        searchable: { label: "Source" },
        width: "220px",
      },
      {
        id: "location",
        label: "Location",
        sortable: true,
        searchable: { label: "Location" },
        width: "180px",
      },
      {
        id: "duration",
        label: "Duration",
        sortable: true,
        searchable: false,
        width: "100px",
      },
      {
        id: "response_code",
        label: "Response Code",
        sortable: true,
        searchable: false,
        width: "120px",
      },
      {
        id: "actions",
        label: "Actions",
        sortable: false,
        searchable: false,
        width: "120px",
      },
    ],
    [],
  );

  const fetchLogsData = useCallback(
    async (dispatch, params) => {
      try {
        const logParams = {
          ...params,
          user_id: id,
          page: params.page || 1,
        };

        // Handle sorting
        if (params.sortBy && params.sortOrder) {
          let orderField = params.sortBy;

          // Map frontend field names to backend field names
          switch (params.sortBy) {
            case "date_time":
              orderField = "date,-start_time";
              break;
            case "type":
              orderField = "type";
              break;
            case "duration":
              orderField = "start_time"; // Backend should calculate duration
              break;
            case "referer":
              orderField = "referer_host";
              break;
            default:
              orderField = params.sortBy;
          }

          logParams.ordering = `${params.sortOrder === "desc" ? "-" : ""}${orderField}`;
        } else {
          logParams.ordering = "-date,-start_time";
        }

        // Handle search parameters
        if (params.endpoint) logParams.endpoint = params.endpoint;
        if (params.referer) logParams.referer = params.referer;
        if (params.type) {
          // Convert user-friendly type to HTTP method
          const methodFromLabel = getMethodFromTypeLabel(params.type);
          console.log(
            "Search type conversion:",
            params.type,
            "->",
            methodFromLabel,
          );
          logParams.type = methodFromLabel;
        }

        // Handle date range filtering
        if (params.date_time_from) {
          logParams.date_from = params.date_time_from;
        }
        if (params.date_time_to) {
          logParams.date_to = params.date_time_to;
        }

        console.log("Final logParams:", logParams);

        const result = await dispatch(getApiRequestLogs(logParams)).unwrap();
        return {
          results: result.results || result,
          count: result.count || (Array.isArray(result) ? result.length : 0),
        };
      } catch (error) {
        console.error("Error fetching logs:", error);
        return { results: [], count: 0, error: true };
      }
    },
    [id, dispatch],
  );

  const getMethodFromTypeLabel = (typeLabel) => {
    if (!typeLabel) return "";

    const normalizedLabel = typeLabel.toLowerCase().trim();

    switch (normalizedLabel) {
      case "view":
        return "GET";
      case "save":
        return "POST"; // For exact match, we'll search for POST first, then PUT separately if needed
      case "update":
        return "PATCH";
      case "delete":
        return "DELETE";
      default:
        // If it's already an HTTP method, return it
        const upperLabel = typeLabel.toUpperCase();
        if (["GET", "POST", "PUT", "PATCH", "DELETE"].includes(upperLabel)) {
          return upperLabel;
        }
        return typeLabel;
    }
  };

  const getRequestTypeLabel = (method) => {
    if (!method) return "View";

    switch (method.toUpperCase()) {
      case "GET":
        return "View";
      case "POST":
        return "Save";
      case "PUT":
        return "Save";
      case "PATCH":
        return "Update";
      case "DELETE":
        return "Delete";
      default:
        return method;
    }
  };

  const mapLogsData = useCallback(
    (logs) => {
      return logs.map((log) => ({
        ...log,
        date_time: formatDate(log.date, log.start_time),
        method: log.method ?? "N/A",
        endpoint: log.endpoint || "N/A",
        referer: log.referer_host || log.referer || "N/A",
        duration: log.duration ?? "N/A",
        response_code: log.response_code ?? "N/A",
        actions: (
          <button
            onClick={() => handleViewDetails(log.id)}
            className="bg-blue-500 text-white px-2 py-1 rounded text-sm hover:bg-blue-600"
          >
            View Details
          </button>
        ),
      }));
    },
    [handleViewDetails],
  );

  const handleRefreshLogs = useCallback(() => {
    // Trigger a fresh fetch with basic parameters
    fetchLogsData(dispatch, {
      page: 1,
      sortBy: "date_time",
      sortOrder: "desc",
    });
  }, [fetchLogsData, dispatch]);

  const formatDate = (dateString, timeString = null) => {
    if (!dateString) return "N/A";
    try {
      if (timeString) {
        // Combine date and time strings
        const combined = `${dateString}T${timeString}`;
        return new Date(combined).toLocaleString();
      } else if (
        typeof dateString === "string" &&
        !dateString.includes("T") &&
        !dateString.includes(" ")
      ) {
        // If it's just a date string (YYYY-MM-DD), format it as date only
        return new Date(dateString + "T00:00:00").toLocaleDateString();
      } else {
        // Handle full datetime strings
        return new Date(dateString).toLocaleString();
      }
    } catch (e) {
      return "N/A";
    }
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return "N/A";
    try {
      // Handle time-only format (HH:MM:SS.microseconds)
      if (
        typeof startTime === "string" &&
        !startTime.includes("T") &&
        !startTime.includes(" ")
      ) {
        // Convert time-only strings to full datetime for calculation
        const today = new Date().toISOString().split("T")[0]; // Get today's date
        const start = new Date(`${today}T${startTime}`);
        const end = new Date(`${today}T${endTime}`);

        if (isNaN(start.getTime()) || isNaN(end.getTime())) return "N/A";

        const duration = end - start;
        return `${duration}ms`;
      } else {
        // Handle full datetime strings
        const start = new Date(startTime);
        const end = new Date(endTime);
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return "N/A";
        const duration = end - start;
        return `${duration}ms`;
      }
    } catch (e) {
      return "N/A";
    }
  };

  const renderJsonInModal = (data) => {
    if (!data) return null;

    try {
      const formatted = typeof data === "string" ? JSON.parse(data) : data;
      return (
        <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto max-h-96 whitespace-pre-wrap">
          {JSON.stringify(formatted, null, 2)}
        </pre>
      );
    } catch (e) {
      // If it's not valid JSON, display as text
      return (
        <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto max-h-96 whitespace-pre-wrap">
          {String(data)}
        </pre>
      );
    }
  };

  if (selectedUserLoading) return <div className="p-4">Loading user...</div>;
  if (selectedUserError)
    return <div className="p-4 text-red-600">{selectedUserError}</div>;
  if (!selectedUser) return null;

  return (
    <div className="w-full p-6 bg-white/40 rounded shadow mx-auto">
      <h2 className="text-xl font-bold mb-4">
        Edit User{" "}
        <Link
          to="/settings/user-admin"
          className="text-sm ml-4 font-medium text-[var(--primary-color)] hover:text-[var(--secondary-color)]"
        >
          Back to all users
          <span aria-hidden="true"> &rarr;</span>
        </Link>{" "}
      </h2>

      {/* Tab Navigation */}
      <div className="mb-6 border-b">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab("details")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "details"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            User Details
          </button>
          <button
            onClick={() => setActiveTab("logs")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "logs"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            API Logs
          </button>
        </nav>
      </div>

      {/* User Details Tab */}
      {activeTab === "details" && (
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700">
              {success}
            </div>
          )}
          {loginAsError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700">
              Login As Error: {loginAsError}
            </div>
          )}
          {loginAsSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700">
              Successfully logged in as user. Redirecting...
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* User Basic Info */}
            <div className="space-y-4 lg:col-span-1">
              <div>
                <label className="block font-medium">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formChanges.email ?? selectedUser?.email ?? ""}
                  onChange={handleChange}
                  className="w-full border rounded px-2 py-1"
                  required
                />
              </div>
              <div>
                <label className="block font-medium">First Name</label>
                <input
                  type="text"
                  name="first_name"
                  value={
                    formChanges.first_name ?? selectedUser?.first_name ?? ""
                  }
                  onChange={handleChange}
                  className="w-full border rounded px-2 py-1"
                />
              </div>
              <div>
                <label className="block font-medium">Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  value={formChanges.last_name ?? selectedUser?.last_name ?? ""}
                  onChange={handleChange}
                  className="w-full border rounded px-2 py-1"
                />
              </div>
              <div>
                <label className="block font-medium">Customer Number</label>
                <input
                  type="text"
                  name="customer_number"
                  value={
                    formChanges.customer_number ??
                    selectedUser?.customer_number ??
                    ""
                  }
                  onChange={handleChange}
                  className="w-full border rounded px-2 py-1"
                  required
                />
              </div>
              <div className="flex items-center gap-4 col-span-2 flex-wrap">
                <CheckBox
                  label="Active User"
                  checked={
                    formChanges.is_active ?? selectedUser?.is_active ?? false
                  }
                  onChange={(checked) =>
                    setFormChanges((prev) => ({ ...prev, is_active: checked }))
                  }
                />
                <CheckBox
                  label="CSFA Site Admin"
                  checked={
                    formChanges.csfa_site_admin ??
                    selectedUser?.csfa_site_admin ??
                    false
                  }
                  onChange={(checked) =>
                    setFormChanges((prev) => ({
                      ...prev,
                      csfa_site_admin: checked,
                    }))
                  }
                />
                <CheckBox
                  label="Force password change on next login"
                  checked={
                    formChanges.must_change_password ??
                    selectedUser?.must_change_password ??
                    false
                  }
                  onChange={(checked) =>
                    setFormChanges((prev) => ({
                      ...prev,
                      must_change_password: checked,
                    }))
                  }
                />
                <CheckBox
                  label="Email validated"
                  checked={
                    formChanges.email_validated ??
                    selectedUser?.email_validated ??
                    false
                  }
                  onChange={(checked) =>
                    setFormChanges((prev) => ({
                      ...prev,
                      email_validated: checked,
                    }))
                  }
                />
                <CheckBox
                  label="Auto Approve"
                  checked={
                    formChanges.csfa_auto_approve ??
                    selectedUser?.csfa_auto_approve ??
                    false
                  }
                  onChange={(checked) =>
                    setFormChanges((prev) => ({
                      ...prev,
                      csfa_auto_approve: checked,
                    }))
                  }
                />
                {selectedUser?.initial_save_log && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start">
                      <svg
                        className="h-5 w-5 text-blue-400 mt-0.5 mr-2 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div>
                        <h4 className="text-sm font-medium text-blue-800 mb-1">
                          Initial Save Log
                        </h4>
                        <p className="text-sm text-blue-700 whitespace-pre-wrap">
                          {selectedUser.initial_save_log}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Permissions */}
            <div className="space-y-4 lg:col-span-1">
              <div className="flex justify-between items-center gap-2 flex-wrap mb-1">
                <label className="block font-medium">Permissions</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded font-semibold shadow-sm hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed border border-green-300"
                    onClick={() =>
                      setPermissions((prev) =>
                        prev.map((perm) => ({ ...perm, has_permission: true })),
                      )
                    }
                    disabled={
                      permissions.length === 0 ||
                      permissions.every((perm) => perm.has_permission)
                    }
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded font-semibold shadow-sm hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed border border-red-300"
                    onClick={() =>
                      setPermissions((prev) =>
                        prev.map((perm) => ({
                          ...perm,
                          has_permission: false,
                        })),
                      )
                    }
                    disabled={
                      permissions.length === 0 ||
                      permissions.every((perm) => !perm.has_permission)
                    }
                  >
                    Unselect All
                  </button>
                </div>
              </div>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {Object.entries(
                  permissions.reduce((groups, perm) => {
                    const category = perm.category || "Other";
                    if (!groups[category]) groups[category] = [];
                    groups[category].push(perm);
                    return groups;
                  }, {}),
                ).map(([category, categoryPermissions]) => (
                  <div
                    key={category}
                    className="border rounded-lg p-3 bg-gray-50"
                  >
                    <h4 className="font-semibold text-sm text-gray-700 border-b border-gray-200 pb-1 m-0">
                      {category}
                    </h4>
                    <div className="grid grid-cols-1 gap-2 mt-2">
                      {categoryPermissions.map((perm) => (
                        <CheckBox
                          key={perm.name}
                          label={perm.description}
                          checked={perm.has_permission}
                          onChange={(checked) => {
                            setPermissions((prev) =>
                              prev.map((p) =>
                                p.name === perm.name
                                  ? { ...p, has_permission: checked }
                                  : p,
                              ),
                            );
                          }}
                          disabled={!perm.editable}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Vendor Categories */}
            <div className="space-y-4 lg:col-span-1">
              <div className="flex justify-between items-center gap-2 flex-wrap">
                <label className="block font-medium">Vendor Categories</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const allVCs = {};
                      vendorCategories.forEach((categoryId) => {
                        allVCs[categoryId] = -1; // -1 means unlimited
                      });
                      setUserVendorCategories(allVCs);
                    }}
                    disabled={
                      vendorCategoriesLoading ||
                      vendorCategories.length === 0 ||
                      Object.keys(userVendorCategories).length ===
                        vendorCategories.length
                    }
                    className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded font-semibold shadow-sm hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed border border-green-300"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => setUserVendorCategories({})}
                    disabled={Object.keys(userVendorCategories).length === 0}
                    className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded font-semibold shadow-sm hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed border border-red-300"
                  >
                    Unselect All
                  </button>
                </div>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                {vendorCategories.length > 0 ? (
                  vendorCategories.map((categoryId) => {
                    const isChecked = categoryId in userVendorCategories;
                    const quantity = userVendorCategories[categoryId] ?? -1;

                    return (
                      <div
                        key={categoryId}
                        className="flex items-center gap-3 py-1"
                      >
                        <CheckBox
                          label={`VC ${categoryId}`}
                          checked={isChecked}
                          onChange={(checked) => {
                            if (checked) {
                              setUserVendorCategories((prev) => ({
                                ...prev,
                                [categoryId]: -1, // Default to unlimited
                              }));
                            } else {
                              setUserVendorCategories((prev) => {
                                const newVCs = { ...prev };
                                delete newVCs[categoryId];
                                return newVCs;
                              });
                            }
                          }}
                        />
                        {isChecked && (
                          <div className="flex items-center gap-2 ml-auto">
                            <label className="text-xs text-gray-600">
                              Qty:
                            </label>
                            <input
                              type="number"
                              value={quantity}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value);
                                setUserVendorCategories((prev) => ({
                                  ...prev,
                                  [categoryId]: value,
                                }));
                              }}
                              className="w-20 border rounded px-2 py-1 text-sm"
                              placeholder="-1"
                              step="0.01"
                              title="Enter -1 for unlimited quantity"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-gray-500 text-sm">
                    {vendorCategoriesLoading
                      ? "Loading vendor categories..."
                      : "No vendor categories available"}
                  </div>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Selected: {Object.keys(userVendorCategories).length} vendor
                categories
                <span className="ml-2 text-gray-400">
                  (Use -1 for unlimited)
                </span>
              </div>
            </div>
          </div>

          <div className="col-span-1 lg:col-span-3 flex gap-4 mt-6 items-center">
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
              disabled={saving || updateVendorCategoriesLoading}
            >
              {saving || updateVendorCategoriesLoading
                ? "Saving..."
                : "Save Changes"}
            </button>
            <button
              type="button"
              className="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            {selectedUser?.is_active && (
              <button
                type="button"
                className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 disabled:opacity-50"
                onClick={handleLoginAs}
                disabled={loginAsLoading}
              >
                {loginAsLoading ? "Logging in..." : "Login As User"}
              </button>
            )}
          </div>
        </form>
      )}

      {/* API Logs Tab */}
      {activeTab === "logs" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              API Request Logs for User ID: {id}
            </h3>
            <button
              onClick={handleRefreshLogs}
              disabled={apiLogsLoading}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Refresh logs"
            >
              <svg
                className={`w-4 h-4 ${apiLogsLoading ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Sync
            </button>
          </div>
          <Grid
            title={`API Request Logs for User ID: ${id}`}
            headerAttributes={logsHeaderAttributes}
            fetchDataFunction={fetchLogsData}
            mapDataFunction={mapLogsData}
            loadingSelector={(state) => state.admin.apiLogsLoading}
            rowsPerPage={25}
            defaultSortBy="date_time"
            defaultSortOrder="desc"
            showFilters={true}
            showDateFilter={false}
            showPagination={true}
          />
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden mx-4">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">API Request Log Details</h3>
              <button
                onClick={closeDetailModal}
                className="text-gray-500 hover:text-gray-700 text-xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              {logDetailLoading && (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2">Loading details...</p>
                </div>
              )}

              {selectedLogDetail && !logDetailLoading && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <strong>ID:</strong> {selectedLogDetail.id}
                    </div>
                    <div>
                      <strong>User ID:</strong>{" "}
                      {selectedLogDetail.user_id || "N/A"}
                    </div>
                    <div>
                      <strong>Date:</strong>{" "}
                      {formatDate(
                        selectedLogDetail.date,
                        selectedLogDetail.start_time,
                      )}
                    </div>
                    <div>
                      <strong>Start Time:</strong>{" "}
                      {selectedLogDetail.start_time || "N/A"}
                    </div>
                    <div>
                      <strong>End Time:</strong>{" "}
                      {selectedLogDetail.end_time || "N/A"}
                    </div>
                    <div>
                      <strong>Duration:</strong>{" "}
                      {formatDuration(
                        selectedLogDetail.start_time,
                        selectedLogDetail.end_time,
                      )}
                    </div>
                    <div className="col-span-full">
                      <strong>API URL:</strong>
                      <div className="mt-1 p-2 bg-gray-100 rounded break-all text-sm">
                        {selectedLogDetail.source || "N/A"}
                      </div>
                    </div>
                    <div className="col-span-full">
                      <strong>Source:</strong>
                      <div className="mt-1 p-2 bg-gray-100 rounded break-all text-sm">
                        {selectedLogDetail.referer || "N/A"}
                      </div>
                    </div>
                  </div>

                  {(selectedLogDetail.query_params ||
                    selectedLogDetail.query_params_json) && (
                    <div>
                      <strong>Query Parameters:</strong>
                      {renderJsonInModal(
                        selectedLogDetail.query_params ||
                          selectedLogDetail.query_params_json,
                      )}
                    </div>
                  )}

                  {(selectedLogDetail.body || selectedLogDetail.body_json) && (
                    <div>
                      <strong>Request Body:</strong>
                      {renderJsonInModal(
                        selectedLogDetail.body || selectedLogDetail.body_json,
                      )}
                    </div>
                  )}

                  {selectedLogDetail.response_data && (
                    <div>
                      <strong>Response Data:</strong>
                      {renderJsonInModal(selectedLogDetail.response_data)}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Login As Confirmation Modal */}
      {showLoginAsConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full mx-4 p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-orange-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Confirm Login As User
                </h3>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-3">
                You are about to log in as:
              </p>
              <div className="bg-gray-50 p-3 rounded border">
                <p className="font-medium text-gray-900">
                  {selectedUser?.first_name} {selectedUser?.last_name}
                </p>
                <p className="text-sm text-gray-600">{selectedUser?.email}</p>
              </div>
              <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded">
                <div className="flex">
                  <svg
                    className="h-5 w-5 text-orange-400 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="ml-2">
                    <p className="text-sm font-medium text-orange-800">
                      Warning: This will log you out
                    </p>
                    <p className="text-sm text-orange-700 mt-1">
                      You will be logged out of your current admin session and
                      logged in as this user. You'll need to log back in as
                      yourself to return to admin functions.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={() => setShowLoginAsConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                onClick={confirmLoginAs}
                disabled={loginAsLoading}
              >
                {loginAsLoading ? "Logging in..." : "Yes, Login As User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
